import { AuthToken, cache, ZomentumStorageKeys } from "@/Contracts";
import { refreshAccessToken } from "@/Components/Utils/accessTokenRefresher";

export class BlockSaveHttpError extends Error {
	readonly status: number;

	constructor(status: number) {
		super(`Saving the block failed with status ${status}`);
		this.name = "BlockSaveHttpError";
		this.status = status;
	}
}

export type BlockSaveFailure = "retrying" | "permanent";

export const worseBlockSaveFailure = (
	first: BlockSaveFailure | null,
	second: BlockSaveFailure | null
): BlockSaveFailure | null =>
	first === "permanent" || second === "permanent"
		? "permanent"
		: first ?? second;

export const isAuthBlockSaveError = (error: unknown): boolean =>
	error instanceof BlockSaveHttpError && error.status === 401;

const RETRYABLE_CLIENT_ERROR_STATUSES = new Set([401, 408, 409, 429]);

export const isRetryableBlockSaveError = (error: unknown): boolean => {
	if (error instanceof BlockSaveHttpError) {
		return (
			error.status >= 500 ||
			RETRYABLE_CLIENT_ERROR_STATUSES.has(error.status)
		);
	}
	return true;
};

const getAuthorizationHeader = (): Record<string, string> => {
	const authToken = cache.get(ZomentumStorageKeys.Token, true) as
		| AuthToken
		| undefined;
	return authToken?.token
		? { Authorization: `Bearer ${authToken.token}` }
		: {};
};

export const putBlock = async (
	blockId: string,
	body: unknown
): Promise<Response> => {
	const send = () =>
		fetch(`${process.env.REACT_APP_API_HOST || ""}/v2/blocks/${blockId}`, {
			method: "PUT",
			headers: {
				...getAuthorizationHeader(),
				"Content-Type": "application/json"
			},
			body: JSON.stringify(body)
		});
	let response = await send();
	if (response.status === 401 && (await refreshAccessToken())) {
		response = await send();
	}
	if (!response.ok) {
		throw new BlockSaveHttpError(response.status);
	}
	return response;
};
